import "./WhatWeDoSection.scss";
import radarChart from "../../assets/icons/radarChartWWD.svg";
import CardWWDComponentLeft from "./CardSection";
import CardWWDComponentRight from "./CardSection";
import { useEffect, useState } from "react";

const panels = ["left-top", "left-bottom", "right-top", "right-bottom"];

const LandingWhatWeDo = () => {
    const [activePanel, setActivePanel] = useState("left-top");
    const [isHovered, setIsHovered] = useState(false);
    const [isIntroVisible, setIsIntroVisible] = useState(false);
    const [isHalfScreen, setIsHalfScreen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 1920/2) {
                setIsHalfScreen(false);
            }
            else setIsHalfScreen(true);
        };

        // Set initial state based on current window width
        handleResize();

        // Add event listener for window resize
        window.addEventListener('resize', handleResize);

        // Cleanup event listener on component unmount
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            if (!isHovered) {
                setActivePanel((prevPanel) => {
                    const currentIndex = panels.indexOf(prevPanel);
                    const nextIndex = (currentIndex + 1) % panels.length;
                    return panels[nextIndex];
                });
            }
        }, 5500);

        return () => clearInterval(interval);
    }, [isHovered]);

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);
    const handleHeaderClick = () => setIsIntroVisible((prev) => !prev);

    const getH2Style = (panel) => {
        return panel === activePanel
            ? {
                color: "black",
                fontWeight: "bold",
                transform: "scale(1.08)",
                transition: ".5s ease-in-out",
            }
            : { color: "gray", transition: ".5s ease-in-out" };
    };

    const isCardVisible = (panel) => panel === activePanel;

    return (
        <section id="what-we-do" className="section section--wwd">
            <h2
                className="wwd-header"
                onClick={handleHeaderClick}
                style={{
                    cursor: "pointer",
                    marginBottom: "1rem",
                    textAlign: "center",
                    color: "#111",
                    transition: "color 0.3s",
                }}
            >
                what we do
            </h2>
            <div className={`wwd-intro-wrapper ${isIntroVisible ? "visible" : ""}`}>
                <div className="wwd-intro">
                    <p><strong>At Kanzu, we create IT products with soul.</strong></p>
                    <p>
                        We develop powerful custom web applications with modern interfaces and robust architecture. From
                        landing pages and AI-powered business tools to complex platforms - we bring digital services to
                        life, from idea to launch and promotion.
                    </p>
                    <p>
                        We turn concepts into working products that deliver real value.
                        But we don’t just write code. At Kanzu, we’re not executors - we’re co-creators. Our team
                        includes analysts, designers, developers, marketers, and managers who know how to listen, think
                        critically, and offer smart solutions.
                    </p>
                    <p>
                        We work transparently, following a teal-style culture, built on trust, self-organization, and
                        true engagement. We treat your projects as if they were our own. Believe in dialogue, shared
                        thinking, and outcomes that endure.
                    </p>
                    <p>
                        We use modern technologies - Python, Golang, Django, React, Vue, PostgreSQL, and more. And we
                        also use website builders. Our products are scalable, stable, and user-friendly. We design
                        interfaces based on real user needs, conduct research, test hypotheses, work in Figma, and care
                        about every detail - from UX to animation and responsiveness.
                    </p>
                    <p>
                        We go beyond development - we do analytics too: automating reports, visualizing data,
                        integrating AI, and building complex integrations with APIs and ERP-systems. If your service
                        needs AI integration or a chatbot - we know how to make it happen.
                    </p>
                    <p>
                        Beyond development, we help companies grow. Our team launches ad campaigns, optimizes SEO, and
                        builds marketing strategies tailored to your business goals. We work with Google Ads, Meta and
                        YouTube - testing, analyzing, and continuously improving.
                    </p>
                    <p>
                        We easily integrate into client workflows, speak the language of both business and development,
                        and take full responsibility for results.
                    </p>
                    <p>
                        <strong>
                            If you're looking for a team that works with honesty, clarity, and
                            real commitment - you've found it.
                        </strong>
                    </p>
                    <p><strong>Welcome to Kanzu!</strong></p>
                </div>
            </div>

            <div className="content">
                {isHalfScreen ? (
                    <div className="mobile-content">
                        <div className="mobile-content-top">
                            {/* Product Card */}
                            <div className={`mobile-card-section ${isCardVisible("left-top") ? "hovered" : ""}`}>
                                <span className="mobile-header">
                                    Product
                                </span>
                                <CardWWDComponentLeft
                                    flex="flex-center"
                                    tags={["Analytics", "Development", "Growth"]}
                                    descText={
                                        "We analyze your business and IT services, improve your product or create a new one, and help it grow using the right technologies, UX, and marketing."
                                    }
                                    buttonText={"create product"}
                                />
                            </div>

                            {/* Design with Soul Card */}
                            <div className={`mobile-card-section ${isCardVisible("left-bottom") ? "hovered" : ""}`}>
                                <span className="mobile-header">
                                    Design with Soul
                                </span>
                                <CardWWDComponentLeft
                                    flex="flex-center"
                                    tags={["Human-centered", "System Design"]}
                                    descText={
                                        "We create branding and aesthetic, user-centered interfaces - from UX research to final design. Every detail has meaning. Beauty meets clarity. Every flow is for people."
                                    }
                                    buttonText={"Branding & UX/UI"}
                                />
                            </div>
                        </div>
                        <div className="mobile-content-bottom">
                            {/* Websites & Web Services Card */}
                            <div className={`mobile-card-section ${isCardVisible("right-top") ? "hovered" : ""}`}>
                                <span className="mobile-header">
                                    Websites & Web Services
                                </span>
                                <CardWWDComponentRight
                                    flex="flex-center"
                                    tags={["Python", "React", "API"]}
                                    descText={
                                        "At Kanzu we're proficient in developing custom web apps as well as websites that create smooth experiences and use flawless technologies."
                                    }
                                    buttonText={"develop chat-bot"}
                                />
                            </div>

                            {/* System Administration Card */}
                            <div className={`mobile-card-section ${isCardVisible("right-bottom") ? "hovered" : ""}`}>
                                <span className="mobile-header">
                                    System Administration
                                </span>
                                <CardWWDComponentRight
                                    flex="flex-center"
                                    tags={["Unix", "Windows", "DBA"]}
                                    descText={
                                        "Our experts are skilled in creating, fine-tuning and maintaining software and computer systems. Complexity is handled with precision and attention."
                                    }
                                    buttonText={"develop admin systems"}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="laptop-content">
                        <div
                            className={`left-panel ${activePanel.startsWith("left") ? "visible visible-left" : ""
                            }`}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <div className="block-left">
                                <h2 className="text-left" style={getH2Style("left-top")}>
                                    Product
                                </h2>
                                <div className="top-panel">
                                    {isCardVisible("left-top") && (
                                        <CardWWDComponentLeft
                                            flex={"flex-center"}
                                            tags={["Analytics", "Development", "Growth"]}
                                            descText={
                                                "We analyze your business and IT services, improve your product or create a new one, and help it grow using the right technologies, UX, and marketing."
                                            }
                                            buttonText={"create product"}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="block-left">
                                <div className="bottom-panel">
                                    {isCardVisible("left-bottom") && (
                                        <CardWWDComponentLeft
                                            flex="flex-center"
                                            tags={["Human-centered", "System Design"]}
                                            descText={
                                                "We create branding and aesthetic, user-centered interfaces - from UX research to final design. Every detail has meaning. Beauty meets clarity. Every flow is for people."
                                            }
                                            buttonText={"Branding & UX/UI"}
                                        />
                                    )}
                                </div>
                                <h2 className="text-left" style={getH2Style("left-bottom")}>
                                    Design with Soul
                                </h2>
                            </div>
                        </div>
                        <div className="center-panel">
                            <div className="radar-chart">
                                <img
                                    src={radarChart}
                                    alt="Radar Chart"
                                    className="radar-chart-image"
                                />
                            </div>
                        </div>
                        <div
                            className={`right-panel ${activePanel.startsWith("right") ? "visible visible-right" : ""
                            }`}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            <div className="block-right">
                                <h2 className="text-right" style={getH2Style("right-top")}>
                                    Websites & Web Services
                                </h2>
                                <div className="top-panel">
                                    {isCardVisible("right-top") && (
                                        <CardWWDComponentRight
                                            flex="flex-center"
                                            tags={["Python", "React", "API"]}
                                            descText={
                                                "At Kanzu we're proficient in developing custom web apps as well as websites that create smooth experiences and use flawless technologies."
                                            }
                                            buttonText={"develop chat-bot"}
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="block-right">
                                <div className="bottom-panel">
                                    {isCardVisible("right-bottom") && (
                                        <CardWWDComponentRight
                                            flex="flex-center"
                                            tags={["Unix", "Windows", "DBA"]}
                                            descText={
                                                "Our experts are skilled in creating, fine-tuning and maintaining software and computer systems. Complexity is handled with precision and attention. "
                                            }
                                            buttonText={"develop admin systems"}
                                        />
                                    )}
                                </div>
                                <h2 className="text-right" style={getH2Style("right-bottom")}>
                                    System Administration
                                </h2>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default LandingWhatWeDo;
